import { BUTLER_TAB } from '@cart-services/constants/butler-tab'
import { cartStoreMap } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bButlerItemsService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-butler-items-service'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bProductsInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-products-info'
import { type ButlerItem } from '@cart-services/types'
import { toPartialCartItem } from '@cart-services/utils/to-partial-cart-item'
import { limitedFrozenSku } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { type CheckboxProps } from '@pets-ui/Checkbox/Checkbox'
import { Container } from '@pets-ui/Container/Container'
import { ErrorFilled, SuccessFilled } from '@pets-ui/icons'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import * as stylex from '@stylexjs/stylex'
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { DialogWeightAttribute, DIALOG_CHECK_WEIGHT_ATTRIBUTE } from '../../../../merchant/product-catalog/components/DialogWeightAttribute/DialogWeightAttribute'
import { butlerTabIconMap, butlerTabLabelMap } from '../../constants'
import { getSubmittableButlerItems } from '../../utils/get-submittable-butler-items'
import { AiRecommendationFooter } from '../AiRecommendationFooter/AiRecommendationFooter'
import { AiRecommendationHeader } from '../AiRecommendationHeader/AiRecommendationHeader'
import { ButlerListItems } from '../ButlerListItems/ButlerListItems'
import { useButler } from '../ButlerProvider/useButler'
import { DialogAiAddToCartError, DIALOG_AI_ADD_TO_CART_ERROR } from '../DialogAiAddToCartError/DialogAiAddToCartError'
import { DialogAiCheckDeliveryClass, DIALOG_AI_CHECK_DELIVERY_CLASS } from '../DialogAiCheckDeliveryClass/DialogAiCheckDeliveryClass'
import { DialogChangeButlerItem } from '../DialogChangeButlerItem/DialogChangeButlerItem'

const styles = stylex.create({
  snackber: {
    backgroundColor: colors.grey800,
    color: colors.white,
    minWidth: '320px',
    width: '100%',
  },
})

export const AiRecommendationContent = () => {
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { openDialog: openDialogAiAddToCartError } = useDialog(DIALOG_AI_ADD_TO_CART_ERROR)
  const { openDialog: openDialogAiCheckDeliveryClass } = useDialog(DIALOG_AI_CHECK_DELIVERY_CLASS)
  const { openDialog: openDialogWeightAttribute } = useDialog(DIALOG_CHECK_WEIGHT_ATTRIBUTE)
  const isMobile = useResponsive('mobile')
  const history = useHistory()
  const { projectCode, countryCode, campaignId, campaignCode, platform, routes, selectedTab, onAddToCartSuccess } = useButler()
  const { overwriteCartItems } = useFpB2bCartService(campaignId, platform)
  const { items: butlerItems, upsertButlerItem, fileName, resetButler } = useFpB2bButlerItemsService(campaignId, platform)
  const { productMap } = useFpB2bProductsInfo({
    projectCode,
    countryCode,
    campaignCode,
    campaignId,
    extraProductIds: butlerItems.map(item => item.variation_id).filter(variationId => variationId !== undefined),
    platform,
  })

  // 檢查是否所有可勾選的項目都已勾選（缺貨的項目視為無法勾選）
  const allItemsChecked = butlerItems.every((item) => {
    const isOutOfStock = item.quantity !== undefined && item.stock !== undefined && item.quantity > item.stock
    const isChecked = item.checked
    // 項目已勾選，或因缺貨而無法勾選
    return isChecked || isOutOfStock
  })

  const handleCheckAllChange: CheckboxProps['onChange'] = (e) => {
    butlerItems.forEach((item) => {
      upsertButlerItem({
        cartItemId: item.cartItemId,
        checked: e.target.checked,
      })
    })
  }
  /**
   * 比照 CSV 一律覆蓋：只換掉該品牌購物車的商品（店家、優惠券、配送保留），再標成已同步。
   * 標已同步有效的前提是兩個平台的 sync hook 對已同步的 campaign 都以 null key 不發 cart_request_cache 請求，
   * 否則購物車頁掛載時會把 session 舊快照以 SKU 合併回來。
   */
  const commitButlerItemsToCart = (selectedButlerItems: ButlerItem[]) => {
    overwriteCartItems(selectedButlerItems.flatMap((item) => {
      const cartItem = toPartialCartItem(item)
      return cartItem ? [cartItem] : []
    }))
    cartStoreMap[platform].markCartSessionSynced(campaignCode)
    onAddToCartSuccess?.()

    openSnackbar({
      xStyles: { snackbar: styles.snackber },
      message: '商品已加入採購',
      icon: <SuccessFilled color={colors.green600} />,
    })
    history.push(routes.cart)
    resetButler()
  }

  const activeButlerItems = butlerItems.filter(item => !item.isDeleted)
  const selectedButlerItems = activeButlerItems.filter(item => item.checked)
  const submittableButlerItems = getSubmittableButlerItems(butlerItems, productMap)
  // 辨識完成導頁後，order_items 回來前一列都沒有；全部取消勾選仍可按，交給既有的錯誤提示
  const isSubmitDisabled = !activeButlerItems.length || (selectedButlerItems.length > 0 && !submittableButlerItems.length)

  const handleAddButlerItemsToCart = () => {
    try {
      if (!selectedButlerItems.length) {
        openDialogAiAddToCartError()
        return
      }

      // NOTE: 檢查要送出的商品 delivery_class 是否一致
      const deliveryClasses = new Set(submittableButlerItems.map((item) => {
        if (!item.variation_id) return null
        const product = productMap?.[item.variation_id]
        return product?.delivery_class
      }),
      )
      if (deliveryClasses.size > 1) {
        openDialogAiCheckDeliveryClass()
        return
      }

      // NOTE: 檢查要送出的商品是否包含紅雞小或紅幼雞小之外還有其他商品
      const selectedSkus = submittableButlerItems.map(item => item.sku).filter(sku => sku !== undefined)
      const hasLimitedFrozenSku = selectedSkus.some(sku => limitedFrozenSku.includes(sku))
      const currentSkuIsNotForbidden = selectedSkus.some(sku => !limitedFrozenSku.includes(sku))
      if (hasLimitedFrozenSku && currentSkuIsNotForbidden) {
        openDialogWeightAttribute()
        return
      }

      commitButlerItemsToCart(submittableButlerItems)
    }
    catch (error) {
      openSnackbar({
        xStyles: { snackbar: styles.snackber },
        message: '商品加入採購失敗，請再試一次！',
        icon: <ErrorFilled color={colors.red600} />,
      })
      console.error('Exchange butler item failed:', error)
    }
  }

  const orderTotal = useMemo(() => {
    return butlerItems.filter(item => !item.isDeleted && item.checked).reduce((total, item) => {
      const product = item.variation_id ? productMap?.[item.variation_id] : undefined
      if (!product || !item.quantity || !product.sale_price) return total
      return total + (item.quantity * Number(product.sale_price))
    }, 0)
  }, [butlerItems, productMap])

  const SelectedTabIcon = butlerTabIconMap[selectedTab]

  return (
    <>
      <Container pb="72px" display="flex" flexDirection="column" gap="10px">
        <AiRecommendationHeader
          onBack={() => history.push(routes.entry)}
          numberOfItems={submittableButlerItems.length}
          orderTotal={orderTotal}
          source={(
            <>
              {SelectedTabIcon && <SelectedTabIcon />}
              <Text variant="subtitle">
                {selectedTab === BUTLER_TAB.FILE && !isMobile
                  ? `${butlerTabLabelMap[selectedTab]} : ${fileName}`
                  : butlerTabLabelMap[selectedTab]}
              </Text>
            </>
          )}
        />
        <ButlerListItems />
      </Container>
      <AiRecommendationFooter allChecked={allItemsChecked} onCheckAllChange={handleCheckAllChange} onSubmit={handleAddButlerItemsToCart} submitDisabled={isSubmitDisabled} />
      <DialogChangeButlerItem />
      <DialogAiAddToCartError />
      <DialogAiCheckDeliveryClass />
      <DialogWeightAttribute withBlackButton />
    </>
  )
}
